// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as bankTypesApi from '../api'

//Tiempo de vida del catálogo de bancos en el store (180 minutos)
export const BANK_TYPES_CACHE_TTL = 180 * 60 * 1000

//El endpoint debe devolver una lista: varias pantallas hacen `banks.length` y `.map` sin `?.`
const asList = (data) => (Array.isArray(data) ? data : [])

//Redux Toolkit rellena `action.error.message` con este literal cuando el thunk se rechaza
//con `rejectWithValue`, que es justo el caso de una caída de red (timeout, CORS, backend
//abajo): sin respuesta HTTP no hay `error.response.data` que pasarle
const RTK_REJECTED_SENTINEL = 'Rejected'

//`error` guarda lo que dijo el backend y nada más. Un fallo sin respuesta no dice nada, así
//que se guarda null en vez del literal en inglés de Redux Toolkit: quien lo muestre pondrá
//su propio texto. Que la petición falló ya lo dice `status`
const readError = (action) => {
    const message = action.error?.message

    return action.payload ?? (message === RTK_REJECTED_SENTINEL ? null : message) ?? null
}

const isLatest = (state, action) => state.requestId === action.meta.requestId

export const getBankTypes = createAsyncThunk('appBankTypes/getBankTypes', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await bankTypesApi.fetchBankTypes(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Evita peticiones duplicadas y reutiliza el caché mientras siga vigente
    condition: (_, { getState }) => {
        const bankTypes = getState()?.bankTypes

        if (!bankTypes) return true
        //Invalidación explícita (enableRequest): pasa aunque haya una petición en vuelo,
        //cuya respuesta se descarta por requestId
        if (bankTypes.isRequested) return true
        //Ya hay una petición en vuelo: los demás se cuelgan de esa
        if (bankTypes.status === 'pending') return false
        //Store vacío. También cubre resetBankTypesData, que es como Cuentas bancarias
        //fuerza el refresco al montar
        if (!bankTypes.data?.length) return true

        return Date.now() - (bankTypes.lastFetch ?? 0) > BANK_TYPES_CACHE_TTL
    }
})

export const appBankTypesSlice = createSlice({
    name: 'appBankTypes',
    initialState: {
        data: [],
        isRequested: true,
        // status: idle | pending | fulfilled | rejected
        status: 'idle',
        error: null,
        lastFetch: null,
        requestId: null
    },
    reducers: {
        resetBankTypesData: (state) => {
            state.data = []
        },
        enableRequest: (state) => {
            state.isRequested = true
        }
    },
    extraReducers: builder => {
        builder.addCase(getBankTypes.pending, (state, action) => {
            state.status = 'pending'
            state.error = null
            //Se consume aquí para que una invalidación durante la petición siga vigente
            state.isRequested = false
            state.requestId = action.meta.requestId
        })
        builder.addCase(getBankTypes.fulfilled, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'fulfilled'
            state.data = asList(action.payload.data)
            state.lastFetch = action.payload.fetchedAt
            state.error = null
        })
        builder.addCase(getBankTypes.rejected, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'rejected'
            state.error = readError(action)
            //Permite reintentar en la siguiente llamada sin esperar al TTL
            state.isRequested = true
        })
    }
})

export const { resetBankTypesData, enableRequest } = appBankTypesSlice.actions

export default appBankTypesSlice.reducer
